import { FC, useState } from "react";
import { useParams } from "react-router-dom";
import { useTrans } from "../../../hooks";
import { useAuthData } from "../../../contexts";
import { Text, HStack, Badge, Button, useToast, Box } from "@chakra-ui/react";
import {
    DataType,
    IKaTableProps,
    ITableInstance,
    Table as KaTable,
    SortingMode,
} from "ka-table";
import { Column, PagingOptions } from "ka-table/models";
import {
    ICellEditorProps,
    ICellTextProps,
    IGroupRowProps,
} from "ka-table/props";
import { EditingMode } from "ka-table/enums";
import {
    AppIcon,
    AppSelect,
    AppSelectOption,
    KaPageSizeSelector,
} from "../../../components";
import {
    InternshipScheduleSkill,
    InternshipScheduleSkillApi,
    InternshipScheduleApi,
} from "../../../apis";

export const InternshipScheduleSkillList: FC<{
    table: ITableInstance;
    paging: PagingOptions;
    list: any[];
    setList: (newList: any[]) => void;
    loadingList?: boolean;
    gestureOptions: AppSelectOption[];
}> = ({
    table,
    paging,
    list,
    gestureOptions,
    loadingList = false,
    setList,
}) => {
    const toast = useToast();
    const { internshipScheduleId } = useParams<{
        internshipScheduleId: string;
    }>();
    const { t } = useTrans();
    const { grant } = useAuthData();
    const isEditable = !grant.isCompanyDirector;

    const columns: Column[] = [
        {
            key: "name",
            title: t("ent.InternshipScheduleSkill:name.label"),
            dataType: DataType.String,
            width: "66%",
            isEditable: false,
            isSortable: false,
        },
        {
            key: "skill.name",
            title: t("padm.SkillPage:text.pageTitle"),
            dataType: DataType.String,
            isEditable: false,
            isSortable: false,
        },
        {
            key: "gestures",
            title: t("ent.InternshipScheduleSkill:gestures.label"),
            dataType: DataType.Object,
            width: "34%",
            isEditable: true,
            isSortable: false,
        },
    ];

    const onSubmitHandler = (
        internshipScheduleSkillId: string,
        payload: InternshipScheduleSkill
    ) => {
        InternshipScheduleSkillApi.createOrUpdate<
            InternshipScheduleSkill,
            InternshipScheduleSkill
        >(internshipScheduleSkillId, payload).then(
            ({ response, errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    toast({
                        title: t("cmn:message.save.success"),
                        status: "success",
                    });
                }
            }
        );
    };

    const CustomGestureEditor = ({
        column,
        rowKeyValue,
        value,
        rowData,
        setList,
    }: ICellEditorProps & { setList: (newList: any[]) => void }) => {
        const { t } = useTrans();
        const selectedOptions = [...value];
        const [selectedGestures, setSelectedGestures] =
            useState<AppSelectOption[]>(selectedOptions);

        const payload = new InternshipScheduleSkill({
            internshipSchedule:
                InternshipScheduleApi.toResourceIRI(internshipScheduleId),
            skillCriteria: rowData["@id"],
            gestures: selectedGestures.map(
                (gesture) => gesture.value
            ) as string[],
        });

        const close = () => {
            table.closeEditor(rowKeyValue, column.key);
        };

        return (
            <>
                <AppSelect<AppSelectOption, true>
                    isClearable
                    isMulti={true}
                    options={gestureOptions}
                    value={selectedGestures || null}
                    placeholder={t(
                        "ent.InternshipScheduleSkill:gestures.placeholder"
                    )}
                    onChange={(e) => {
                        setSelectedGestures(e as AppSelectOption[]);
                    }}
                    styles={{
                        container: (base) => ({
                            ...base,
                            maxWidth: "550px",
                        }),
                    }}
                />
                <HStack mt={3} justifyContent={"flex-end"}>
                    <Button size={"xs"} variant={"outline"} onClick={close}>
                        {t("cmn:button.cancel")}
                    </Button>
                    <Button
                        size={"xs"}
                        onClick={() => {
                            const updatedRow = {
                                ...rowData,
                                gestures: selectedGestures,
                            };

                            const newList = list.map((item) =>
                                item.id === rowKeyValue ? updatedRow : item
                            );

                            setList(newList);
                            onSubmitHandler(
                                rowData.internshipScheduleSkillId,
                                payload
                            );
                            close();
                        }}
                    >
                        {t("cmn:button.save")}
                    </Button>
                </HStack>
            </>
        );
    };

    const kaTableProps: IKaTableProps = {
        table: table,
        columns: columns,
        data: list || [],
        loading: {
            enabled: loadingList,
        },
        paging: paging,
        rowKeyField: "id",
        sortingMode: SortingMode.SingleRemote,
        groups: [{ columnKey: "skill.name" }],
        childComponents: {
            pagingSizes: {
                content: (props) => <KaPageSizeSelector {...props} />,
            },
            cellEditor: {
                content: (props: ICellEditorProps) => {
                    switch (props.column.key) {
                        case "gestures":
                            return (
                                <CustomGestureEditor
                                    {...props}
                                    setList={setList}
                                />
                            );
                    }
                },
            },
            groupCell: {
                content: (props: IGroupRowProps) => {
                    switch (props.column.key) {
                        case "skill.name":
                            return (
                                <Text
                                    textStyle={"mediumSm"}
                                    color={"var(--chakra-colors-black)"}
                                    whiteSpace={"collapse"}
                                >
                                    {props?.groupKey[0]}
                                </Text>
                            );
                    }
                },
            },
            cellText: {
                content: (props: ICellTextProps) => {
                    switch (props?.column?.key) {
                        case "name":
                            return (
                                <>
                                    <Text
                                        textStyle={"regularSm"}
                                        color={"var(--chakra-colors-black)"}
                                        whiteSpace={"collapse"}
                                    >
                                        {props?.rowData?.name}
                                    </Text>
                                </>
                            );
                        case "gestures":
                            return (
                                <>
                                    <HStack cursor={"pointer"}>
                                        {isEditable && (
                                            <Box>
                                                <AppIcon
                                                    name={"icl-pencil"}
                                                    color={
                                                        "var(--chakra-colors-primary)"
                                                    }
                                                />
                                            </Box>
                                        )}
                                        <HStack
                                            cursor={"pointer"}
                                            flexWrap="wrap"
                                        >
                                            {props?.rowData?.gestures.map(
                                                (gesture) => (
                                                    <Badge
                                                        key={gesture.value}
                                                        variant="secondary"
                                                        maxW="360px"
                                                        isTruncated
                                                    >
                                                        {gesture?.label}
                                                    </Badge>
                                                )
                                            )}
                                        </HStack>
                                    </HStack>
                                </>
                            );
                    }
                },
            },
        },
    };

    if (isEditable) {
        kaTableProps.editableCells = [
            { columnKey: "gestures", rowKeyValue: 0 },
        ];
        kaTableProps.editingMode = EditingMode.Cell;
    }

    return <KaTable {...kaTableProps} />;
};
